import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError, fileUrl, formatTanggal } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_GURU } from "@/lib/nav";
import { toast } from "sonner";
import { FilterBar } from "@/components/FilterBar";
import { BukuDigitalReadonlyList, BukuDigitalReader } from "@/pages/pustaka/BukuDigitalShared";

/* ═══════════════════════ DAFTAR BUKU FISIK (Guru) ═══════════════════════ */
const F_BUKU = { q: "", penulis: "", kategori: "" };

export function PerpustakaanGuruFisik() {
  const [books, setBooks] = useState([]);
  const [filters, setFilters] = useState(F_BUKU);
  const [applied, setApplied] = useState(F_BUKU);
  const [showPinjam, setShowPinjam] = useState(null);
  const [lama, setLama] = useState(3);

  const load = useCallback(async () => {
    const { data } = await api.get("/buku", { params: { format: "fisik" } });
    setBooks(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = books.filter((b) => {
    if (applied.q && !b.judul.toLowerCase().includes(applied.q.toLowerCase())) return false;
    if (applied.penulis && !(b.penulis || "").toLowerCase().includes(applied.penulis.toLowerCase())) return false;
    if (applied.kategori && b.kategori !== applied.kategori) return false;
    return true;
  });

  const pinjam = async () => {
    try { await api.post("/peminjaman/request", { buku_id: showPinjam.id, lama_pinjam: lama }); toast.success("Permintaan peminjaman terkirim"); setShowPinjam(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Layout nav={NAV_GURU}>
      <PageTitle title="Daftar Buku Fisik" />
      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(F_BUKU); setApplied(F_BUKU); }}
        dataTestidPrefix="g-buku-filter"
        fields={[
          { key: "q", type: "text", label: "Judul", placeholder: "Cari judul" },
          { key: "penulis", type: "text", label: "Penulis", placeholder: "Cari penulis" },
          { key: "kategori", type: "select", label: "Kategori", options: [{ v: "buku_teks_pelajaran", label: "Buku Teks Pelajaran" }, { v: "buku_umum", label: "Buku Umum" }] },
        ]}
      />
      <section className="pp-card pp-freeze overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="pp-table-header">
            <th className="text-left px-5 py-3">Cover</th>
            <th className="text-left px-5 py-3">Judul</th>
            <th className="text-left px-5 py-3">Penulis</th>
            <th className="text-left px-5 py-3">Kategori</th>
            <th className="text-center px-5 py-3">Ready</th>
            <th className="text-right px-5 py-3">Aksi</th>
          </tr></thead>
          <tbody>
            {visible.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-400 italic">Belum ada buku fisik.</td></tr>}
            {visible.map(b => (
              <tr key={b.id} className="pp-row">
                <td className="px-5 py-3">{b.cover_file_id ? <img src={fileUrl(b.cover_file_id)} alt="" className="w-10 h-14 object-cover rounded" /> : <div className="w-10 h-14 bg-slate-100 rounded" />}</td>
                <td className="px-5 py-3 font-medium">{b.judul}</td>
                <td className="px-5 py-3 text-slate-600">{b.penulis}</td>
                <td className="px-5 py-3 text-xs">{b.kategori.replace("_", " ")} · {b.jenis_buku}</td>
                <td className="px-5 py-3 text-center">{b.ready}/{b.jumlah}</td>
                <td className="px-5 py-3 text-right"><button onClick={() => { setShowPinjam(b); setLama(3); }} disabled={b.ready < 1} className="pp-btn-secondary text-xs disabled:opacity-50" data-testid={`pinjam-${b.id}`}>Pinjam</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {showPinjam && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowPinjam(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-3">Pinjam Buku</h3>
            <p className="text-sm text-slate-600 mb-4">{showPinjam.judul}</p>
            <label className="pp-label">Lama Pinjam (hari)</label>
            <select className="pp-input mb-4" value={lama} onChange={(e) => setLama(parseInt(e.target.value, 10))}>
              {[1,2,3,4,5,6,7].map(d => <option key={d} value={d}>{d} hari</option>)}
            </select>
            <div className="flex justify-end gap-2"><button onClick={() => setShowPinjam(null)} className="pp-btn-secondary">Batal</button><button onClick={pinjam} className="pp-btn-primary">Konfirmasi</button></div>
          </div>
        </div>
      )}
    </Layout>
  );
}

/* ═══════════════════════ DAFTAR BUKU DIGITAL (Guru) ═══════════════════════ */
export function PerpustakaanGuruDigital() {
  return <BukuDigitalReadonlyList nav={NAV_GURU} basePath="/guru/perpustakaan/digital" title="Daftar Buku Digital" />;
}

export function PerpustakaanGuruBaca() {
  return <BukuDigitalReader nav={NAV_GURU} backPath="/guru/perpustakaan/digital" />;
}

/* ═══════════════════════ DAFTAR PEMINJAMAN SAYA (Guru) ═══════════════════════ */
const F_LOAN = { judul: "", bulan: "", tahun: "", status: "" };

export function PerpustakaanGuruPeminjaman() {
  const [loans, setLoans] = useState([]);
  const [filters, setFilters] = useState(F_LOAN);
  const [applied, setApplied] = useState(F_LOAN);

  const load = useCallback(async () => {
    const { data } = await api.get("/peminjaman");
    setLoans(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const items = loans.filter((l) => {
    if (applied.judul && !(l.judul_buku || "").toLowerCase().includes(applied.judul.toLowerCase())) return false;
    // Use tanggal_pinjam if ada, else tanggal_request
    const refDate = l.tanggal_pinjam || l.tanggal_request || "";
    if (applied.bulan && refDate.slice(5, 7) !== applied.bulan) return false;
    if (applied.tahun && refDate.slice(0, 4) !== String(applied.tahun)) return false;
    if (applied.status && l.status !== applied.status) return false;
    return true;
  });

  const badge = (s) => {
    const map = {
      dipinjam: "pp-badge-info",
      dikembalikan: "pp-badge-success",
      pending: "pp-badge-warning",
      ditolak: "pp-badge-danger",
    };
    return <span className={map[s] || "pp-badge-warning"}>{s}</span>;
  };

  return (
    <Layout nav={NAV_GURU}>
      <PageTitle title="Daftar Peminjaman Saya" />
      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(F_LOAN); setApplied(F_LOAN); }}
        dataTestidPrefix="g-loan-filter"
        fields={[
          { key: "judul", type: "text", label: "Judul", placeholder: "Cari judul buku" },
          { key: "bulan", type: "bulan", label: "Bulan" },
          { key: "tahun", type: "tahun", label: "Tahun" },
          { key: "status", type: "select", label: "Status", options: [
            { v: "dipinjam", label: "Dipinjam" },
            { v: "dikembalikan", label: "Dikembalikan" },
            { v: "pending", label: "Pending" },
            { v: "ditolak", label: "Ditolak" },
          ] },
        ]}
      />
      <section className="pp-card pp-freeze overflow-hidden">
        <table className="w-full text-sm" data-testid="g-loan-table">
          <thead><tr className="pp-table-header">
            <th className="text-left px-5 py-3">Judul</th>
            <th className="text-left px-5 py-3">Tgl Request</th>
            <th className="text-left px-5 py-3">Tgl Pinjam</th>
            <th className="text-left px-5 py-3">Tgl Kembali</th>
            <th className="text-center px-5 py-3">Lama (hari)</th>
            <th className="text-left px-5 py-3">Status</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-400 italic">Belum ada peminjaman.</td></tr>}
            {items.map(l => (
              <tr key={l.id} className="pp-row">
                <td className="px-5 py-3 font-medium">{l.judul_buku}</td>
                <td className="px-5 py-3 font-mono text-xs">{formatTanggal(l.tanggal_request)}</td>
                <td className="px-5 py-3 font-mono text-xs">{l.tanggal_pinjam ? formatTanggal(l.tanggal_pinjam) : "—"}</td>
                <td className="px-5 py-3 font-mono text-xs">{l.tanggal_kembali ? formatTanggal(l.tanggal_kembali) : "—"}</td>
                <td className="px-5 py-3 text-center">{l.lama_pinjam}</td>
                <td className="px-5 py-3">{badge(l.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </Layout>
  );
}

// Backward-compat alias (kept so old imports don't break; renders Fisik page)
export const PerpustakaanGuru = PerpustakaanGuruFisik;
